import React from "react";
import { Line } from 'react-chartjs-2';
import Card from 'react-bootstrap/Card';
import { useTranslation } from 'react-i18next';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement, // Added for completeness
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import 'chart.js/auto';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

const RegionalLineChartTimelinePolicies = (props) => {
  const { t } = useTranslation();
  
  // 1. DYNAMIC YEAR GENERATION
  const startYear = 1990;
  const currentYear = new Date().getFullYear(); // This will be 2026
  
  const allYears = Array.from(
    { length: currentYear - startYear + 1 },
    (_, i) => startYear + i
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 42, right: 22, left: 22 }
    },
    scales: {
      x: {
        display: true,
        ticks: {
          callback: function(val, index) {
            const year = allYears[index];
            const isCurrentYear = year === currentYear;
            const isEveryFive = year % 5 === 0;
            
            // Logic: Show Current Year and Multiples of 5 (avoiding the 2025/2026 overlap)
            if (isCurrentYear) return year;
            if (isEveryFive && year < currentYear - 1) return year;
            
            return '';
          },
          maxRotation: 0,
          autoSkip: false,
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: t("graphA11_ybar_label1"),
          color: "rgb(84, 84, 84)",
          font: { size: 12, weight: "500" },
          padding: { top: 30 }
        }
      }
    },
    plugins: {
      legend: {
        display: true,
        onClick: null,
        position: "bottom",
        align: props.size.x > 600 ? "center" : "start",
        labels: { padding: props.size.x > 600 ? 20 : 15 }
      },
      title: {
        display: true,
        align: 'center',
        text: t(props.titleX),
        position: 'bottom',
        color: "rgb(84, 84, 84)",
        font: { size: 14, weight: "500" },
      },
      // 2. DYNAMIC DATALABELS (Every 5 years + Current)
      datalabels: {
        display: false,
        // display: (context) => {
        //   const year = allYears[context.dataIndex];
        //   return year % 5 === 0 || year === currentYear;
        // },
        // align: 'top',
        // anchor: 'end',
        // formatter: (value) => value, // Shows the data value at that point
        // color: "black",
        // font: { size: 11, weight: "500" }
      },
    },
  };

  const regionsLabels = [
    t("region_africa"), t("region_asia"), t("region_europe"), 
    t("region_lac"), t("region_neareast"), t("region_north_america"), 
    t("region_swpacific")
  ];

  const regionColors = [
    "rgb(238,188,188)", "rgb(238,222,171)", "rgb(200,236,181)",
    "rgb(188,194,242)", "rgb(232,182,238)", "rgb(255,102,102)", 
    "rgb(255,170,170)"
  ];

  // 3. TRANSFORM DATA FOR EVERY YEAR
  const DataForLineChart = regionsLabels.map((region, index) => {
    // We map over allYears to ensure the line has a point for every single year
    const dataForOneRegion = allYears.map((year) => {
      // Logic: count elements in props.data where the value is less than the current year
      return props.data[index]?.filter(elem => year > parseInt(elem.value)).length || 0;
    });

    return {
      label: region,
      data: dataForOneRegion,
      backgroundColor: regionColors[index],
      borderColor: regionColors[index],
      fill: false,
      tension: 0.1, // Smooths the line slightly
      pointRadius: (context) => {
          const year = allYears[context.dataIndex];
          // Only show dots on the 5-year marks and current year to keep it clean
          return (year % 5 === 0 || year === currentYear) ? 1 : 0;
      }
    };
  });

  const dataSet = {
    labels: allYears, // Use the dynamic array
    datasets: DataForLineChart
  };

  return (
    <div>
      <Card className="card-padding">
        <Card.Header className="container-header card-title">
          <p className="vertical-center">{t(props.graphTitle)}</p>
        </Card.Header>
        <Card.Body>
          {props.data && (
            <div className={props.isSubquestion ? "graph-div-subquestion" : "graph-div-main"}>
              <Line options={options} data={dataSet} className="graph-height-mobile" />
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default RegionalLineChartTimelinePolicies;